import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { closeOrder, getOrder, refreshOrder } from '../api/orders';
import { ApiError } from '../api/client';
import { apiBaseUrl } from '../lib/env';
import type { Order } from '../types/api';
import {
  formatAmount,
  formatDateTPE,
  formatDateTimeTPE,
  FLOW_LABEL,
  STATUS_LABEL,
} from '../lib/format';
import { StatusBadge } from '../components/StatusBadge';

const CANCEL_REASONS = [
  '客戶反悔',
  '商品有問題',
  '重複下單',
  '客戶要求改其他付款方式',
  '其他',
] as const;

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionState, setActionState] = useState<{ kind: 'idle' | 'busy'; msg?: string }>({
    kind: 'idle',
  });
  const [closeDialog, setCloseDialog] = useState(false);
  const [reasonChoice, setReasonChoice] = useState<string>(CANCEL_REASONS[0]);
  const [reasonOther, setReasonOther] = useState('');
  const [copied, setCopied] = useState(false);

  const reload = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getOrder(id);
      setOrder(data);
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleRefresh = async () => {
    if (!id) return;
    setActionState({ kind: 'busy', msg: '查詢中…' });
    try {
      const updated = await refreshOrder(id);
      setOrder(updated);
      setActionState({ kind: 'idle' });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'RATE_LIMITED') {
        const sec = (e.details as { retryAfterSeconds?: number } | undefined)?.retryAfterSeconds;
        setActionState({
          kind: 'idle',
          msg: `冷卻中，請 ${sec ?? '幾'} 秒後再試`,
        });
      } else {
        setActionState({
          kind: 'idle',
          msg: e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message,
        });
      }
    }
  };

  const handleClose = async () => {
    if (!id) return;
    const reason = reasonChoice === '其他' ? reasonOther.trim() : reasonChoice;
    if (!reason || reason.length > 100) {
      setActionState({ kind: 'idle', msg: '取消原因 1-100 字元' });
      return;
    }
    setActionState({ kind: 'busy', msg: '關閉中…' });
    try {
      await closeOrder(id, { cancelReason: reason });
      await reload();
      setCloseDialog(false);
      setActionState({ kind: 'idle' });
    } catch (e) {
      setActionState({
        kind: 'idle',
        msg: e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message,
      });
    }
  };

  const handleCopyLink = async () => {
    if (!order?.paymentUrl) return;
    const fullUrl = order.paymentUrl.startsWith('http')
      ? order.paymentUrl
      : `${window.location.origin}${order.paymentUrl}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback：選取 + 提示
      window.prompt('複製此連結：', fullUrl);
    }
  };

  if (loading) {
    return (
      <div className="rounded border bg-white p-8 text-center text-sm text-gray-500">載入中…</div>
    );
  }

  if (error || !order) {
    return (
      <div className="rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        <div>載入失敗：{error ?? '訂單不存在'}</div>
        <Link to="/" className="mt-3 inline-block text-rose-700 underline">
          ← 回列表
        </Link>
      </div>
    );
  }

  // spec §2.8 修訂 2026-05-17：流程 B 取號後也可列印
  const canPrintReceipt =
    order.status !== 'failed' && order.virtualAccount !== null;
  const canClose = order.status === 'pending';
  const canRefresh = order.status === 'pending' && order.tradeNo !== null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <Link to="/" className="text-blue-600 hover:underline">
          ← 訂單列表
        </Link>
      </div>

      <div className="rounded-lg border bg-white shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b p-4">
          <div>
            <div className="font-mono text-sm text-gray-500">{order.merTradeNo}</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
              {order.customerName}
            </div>
            <div className="mt-0.5 text-sm text-gray-600">{order.prodDesc}</div>
          </div>
          <StatusBadge status={order.status} />
        </div>

        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 p-4 text-sm sm:grid-cols-2">
          <Field label="流程">{FLOW_LABEL[order.flowType]}</Field>
          <Field label="金額">
            <span className="font-semibold text-stamp">{formatAmount(order.amount)}</span>
          </Field>
          {order.flowType === 'A' && (
            <>
              <Field label="收款銀行">
                {order.bankName ?? '—'}
                {order.bankType && (
                  <span className="ml-1 text-xs text-gray-400">({order.bankType})</span>
                )}
              </Field>
              <Field label="虛擬帳號">
                <span className="font-mono">{order.virtualAccount ?? '—'}</span>
              </Field>
            </>
          )}
          <Field label="繳費期限">
            {formatDateTPE(order.expireAt)}
            <span className="ml-1 text-xs text-gray-400">（{order.expireDays} 天）</span>
          </Field>
          {order.payTime && <Field label="付款時間">{formatDateTimeTPE(order.payTime)}</Field>}
          <Field label="建立">
            {formatDateTimeTPE(order.createdAt)}
            <span className="ml-1 text-xs text-gray-400">by {order.createdBy}</span>
          </Field>
          {order.flowType === 'B' && (
            <Field label="連結點擊">{order.linkClicks} 次</Field>
          )}
          {order.cancelReason && <Field label="取消原因">{order.cancelReason}</Field>}
          {order.lastError && (
            <Field label="錯誤訊息">
              <span className="text-rose-700">{order.lastError}</span>
            </Field>
          )}
          {order.lastRefreshedAt && (
            <Field label="上次重查">{formatDateTimeTPE(order.lastRefreshedAt)}</Field>
          )}
        </dl>

        <div className="flex flex-wrap items-center gap-2 border-t p-4">
          {canPrintReceipt && (
            <a
              href={`${apiBaseUrl()}/api/orders/${order.id}/receipt`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-100"
            >
              列印收款單
            </a>
          )}
          {order.flowType === 'B' && order.paymentUrl && (
            <button
              type="button"
              onClick={handleCopyLink}
              className="rounded border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-100"
            >
              {copied ? '✅ 已複製' : '複製連結'}
            </button>
          )}
          {canRefresh && (
            <button
              type="button"
              disabled={actionState.kind === 'busy'}
              onClick={handleRefresh}
              className="rounded border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50"
            >
              重查狀態
            </button>
          )}
          {canClose && (
            <button
              type="button"
              disabled={actionState.kind === 'busy'}
              onClick={() => {
                setActionState({ kind: 'idle' });
                setReasonChoice(CANCEL_REASONS[0]);
                setReasonOther('');
                setCloseDialog(true);
              }}
              className="rounded border border-rose-300 bg-white px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 disabled:opacity-50"
            >
              關閉訂單
            </button>
          )}
        </div>

        {actionState.msg && (
          <div className="border-t bg-amber-50 px-4 py-2 text-xs text-amber-800">
            {actionState.msg}
          </div>
        )}
      </div>

      {/* 關閉訂單 dialog */}
      {closeDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <h2 className="text-lg font-semibold text-gray-900">關閉訂單</h2>
            <p className="mt-1 text-sm text-gray-500">
              將此訂單狀態標記為「{STATUS_LABEL.closed}」並通知 PAYUNi。
            </p>

            <div className="mt-4 space-y-2 text-sm">
              <label className="block text-xs text-gray-500">取消原因</label>
              <select
                value={reasonChoice}
                onChange={(e) => setReasonChoice(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-2"
              >
                {CANCEL_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              {reasonChoice === '其他' && (
                <textarea
                  value={reasonOther}
                  onChange={(e) => setReasonOther(e.target.value)}
                  maxLength={100}
                  placeholder="請輸入原因（最多 100 字）"
                  className="w-full rounded border border-gray-300 px-2 py-2"
                  rows={2}
                />
              )}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCloseDialog(false)}
                className="rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleClose}
                disabled={actionState.kind === 'busy'}
                className="rounded bg-rose-600 px-3 py-2 text-sm text-white hover:bg-rose-700 disabled:opacity-50"
              >
                確認關閉
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-gray-900">{children}</dd>
    </div>
  );
}
